import React, {useState} from "react";

import {faCog, faUser, faUsers} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import CustomButton from "~/component/common/CustomButton";
import GroupContent from "~/component/hub/group/GroupContent";
import ProfileContent from "~/component/hub/profile/ProfileContent";
import SettingsContent from "~/component/hub/settings/SettingsContent";
import {useGroup} from "~/contexts/group/GroupProvider";

import useModal from "../../hooks/useModal";

type Tab = "profile" | "settings" | "group";

const UserHubModalButton: React.FC = () => {
  const {openModal, closeModal, Modal} = useModal();
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const {isCoopMode} = useGroup(); // We still need this to pass down

  return (
    <>
      <CustomButton
        onClick={() => {
          setActiveTab(isCoopMode ? "group" : "profile");
          openModal();
        }}
        aria-label="Open User Hub"
        className="p-2 rounded-full hover:bg-black hover:bg-opacity-70 transition-colors"
      >
        <FontAwesomeIcon
          icon={isCoopMode ? faUsers : faUser}
          size="xl"
          className="text-white p-2 -m-2 rounded-full bg-gradient-to-br from-blue-600 via-blue-500 to-black"
        />
      </CustomButton>

      <Modal>
        <div className="w-full max-w-md text-white">
          {/* Tab Navigation - The Group tab is now always visible */}
          <div className="flex justify-center border-b border-gray-700 mb-6 space-x-8">
            <TabButton
              label="Group"
              icon={faUsers}
              isActive={activeTab === "group"}
              onClick={() => setActiveTab("group")}
            />
            <TabButton
              label="Profile"
              icon={faUser}
              isActive={activeTab === "profile"}
              onClick={() => setActiveTab("profile")}
            />
            <TabButton
              label="Settings"
              icon={faCog}
              isActive={activeTab === "settings"}
              onClick={() => setActiveTab("settings")}
            />
          </div>

          {/* Tab Content */}
          <div className="px-2">
            {activeTab === "profile" && (
              <ProfileContent closeModal={closeModal} />
            )}
            {activeTab === "settings" && <SettingsContent />}
            {/* The GroupContent component itself will handle what to show */}
            {activeTab === "group" && <GroupContent />}
          </div>
        </div>
      </Modal>
    </>
  );
};

// A helper component for the icon tabs to keep the main component clean
const TabButton: React.FC<{
  label: string;
  icon: any;
  isActive: boolean;
  onClick: () => void;
}> = ({label, icon, isActive, onClick}) => (
  <div className="relative group flex flex-col items-center">
    <button
      onClick={onClick}
      className={`p-3 transition-colors duration-300 ${
        isActive ? "text-blue-500" : "text-gray-400 hover:text-white"
      }`}
    >
      <FontAwesomeIcon icon={icon} size="lg" />
    </button>
    {/* The active indicator line */}
    <div
      className={`absolute bottom-0 h-0.5 bg-blue-500 transition-all duration-300 ${
        isActive ? "w-full" : "w-0"
      }`}
    />
    {/* Tooltip */}
    <div className="absolute top-full mt-2 hidden group-hover:block bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
      {label}
    </div>
  </div>
);

export default UserHubModalButton;
